import { useEffect, useState } from "react";
import { adminApi } from "../../lib/adminApi";
import { formatMoney } from "../../lib/pricing";
import { isoToEtLongDate } from "../../lib/time";

export default function AdminCustomerList() {
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    adminApi
      .get("/admin/customers", { params: search ? { search } : {} })
      .then((r) => setCustomers(r.data))
      .catch(() => setCustomers([]))
      .finally(() => setLoading(false));
  }, [search]);

  async function openCustomer(email) {
    try {
      const r = await adminApi.get(`/admin/customers/${encodeURIComponent(email)}`);
      setSelected(r.data);
    } catch { /* ignore */ }
  }

  return (
    <div data-testid="admin-customer-list">
      <div>
        <div className="hbe-eyebrow">Customers</div>
        <h1 className="mt-2 font-serif text-3xl md:text-4xl">Customers</h1>
      </div>

      <input
        className="hbe-input mt-6 max-w-md"
        placeholder="Search name / email / phone"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        data-testid="cust-search"
      />

      {loading ? (
        <div className="mt-6 text-hbe-ink/50">Loading…</div>
      ) : customers.length === 0 ? (
        <div className="mt-6 hbe-card p-6 text-hbe-ink/60" data-testid="cust-empty">
          No customers yet. Once someone books, they'll show up here.
        </div>
      ) : (
        <div className="mt-6 overflow-x-auto hbe-card">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-hbe-ink/55 border-b border-hbe-line">
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Name(s)</th>
                <th className="px-4 py-3">Bookings</th>
                <th className="px-4 py-3">Total spent</th>
                <th className="px-4 py-3">Last booking</th>
              </tr>
            </thead>
            <tbody>
              {customers.map((c) => (
                <tr
                  key={c.email}
                  className="border-b border-hbe-line/50 last:border-0 hover:bg-hbe-soft/30 cursor-pointer"
                  onClick={() => openCustomer(c.email)}
                  data-testid={`cust-row-${c.email}`}
                >
                  <td className="px-4 py-3 text-hbe-primary">{c.email}</td>
                  <td className="px-4 py-3">{(c.name_variants || []).join(", ")}</td>
                  <td className="px-4 py-3">{c.booking_count}</td>
                  <td className="px-4 py-3">{formatMoney(c.total_spent || 0)}</td>
                  <td className="px-4 py-3">
                    {c.last_booking_date ? isoToEtLongDate(c.last_booking_date) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <div
          className="fixed inset-0 z-50 bg-black/40 backdrop-blur-[2px] flex justify-end"
          onClick={() => setSelected(null)}
          role="dialog"
          aria-modal="true"
          data-testid="cust-drawer"
        >
          <div
            className="bg-white w-full max-w-md h-full overflow-auto p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-baseline justify-between">
              <div>
                <div className="hbe-eyebrow">Customer</div>
                <div className="font-serif text-xl text-hbe-primary mt-1">{selected.email}</div>
              </div>
              <button onClick={() => setSelected(null)} className="text-hbe-ink/50" aria-label="Close">✕</button>
            </div>
            <div className="mt-4 text-sm text-hbe-ink/70">
              {selected.booking_count} bookings · {formatMoney(selected.total_spent || 0)} total
            </div>
            <div className="mt-6 space-y-3">
              {(selected.bookings || []).map((b) => (
                <div key={b.id} className="hbe-card p-4 text-sm">
                  <div className="font-mono text-xs text-hbe-primary">{b.confirmation_number}</div>
                  <div className="text-hbe-ink/70 mt-1">
                    {isoToEtLongDate(b.start_datetime)} · {formatMoney(b.price_total)} · {b.booking_status}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
